import { useEffect } from 'react'

import {
  Box,
  Button,
  Divider,
  Flex,
  Link,
  Spinner,
  useDisclosure,
} from '@chakra-ui/react'
import currency from 'currency.js'
import { format } from 'date-fns'
import { isNumber } from 'lodash'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'

import { NextLink } from 'components/common/NextLink'
import { PublicFileLinks } from 'constants/publicFiles'
import { Route } from 'constants/routes'
import { SubscriptionPlans } from 'constants/subscription'
import {
  Subscription,
  SubscriptionTier,
  useBespokeTokenCheckLazyQuery,
  useInvoicesQuery,
  useSubscriptionQuery,
  useSuccessFeeInvoiceListQuery,
  useUpgradeToBespokeMutation,
} from 'generated/generated-graphql'
import { useHasActiveSubscription } from 'hooks/useActiveSubscription'
import { useRegion } from 'providers/RegionProvider'
import { useAuth } from 'providers/UserProvider'
import { EnterpriseInfoModal } from 'routes/Dashboard/Dashboards/EnterpriseInfoModal'
import { EnterpriseSubmitModal } from 'routes/Profile/SubscriptionOverview/EnterpriseSubmitModal'
import { useSuccessToast } from 'utils/toast'
import { unixSecondsToMilliseconds } from 'utils/unixSecondsToMilliseconds'

import { PaymentCard } from './PaymentCard'
import { PlanCard } from './PlanCard'
import { WarningBar } from './WarningBar'
import { ProfileContainerChildrenSpaces } from '../ProfileContainer'

export enum StripeUserType {
  Active = 'active',
  WithoutStripe = 'withoutStripe',
  Expired = 'expired',
}

export const SubscriptionTiersToTagLabels = {
  [SubscriptionTier.Premium]: 'TRANSLATION_126',
  [SubscriptionTier.Standard]: 'TRANSLATION_364',
  [SubscriptionTier.Enterprise]: 'TRANSLATION_127',

  [SubscriptionTier.FreeTrial]: 'Subscriptions.FreeTrial.name',
  [SubscriptionTier.EnterpriseNew]: 'Subscriptions.Enterprise.name',
  [SubscriptionTier.Growth]: 'Subscriptions.Growth.name',
  [SubscriptionTier.Pro]: 'Subscriptions.Pro.name',
}

const FullWidthStyles = {
  mx: {
    base: -ProfileContainerChildrenSpaces.mx.base,
    md: -ProfileContainerChildrenSpaces.mx.md,
  },
  mt: -ProfileContainerChildrenSpaces.my,
  mb: ProfileContainerChildrenSpaces.my,
}

export const getStripeUserType = (subscription: Subscription | undefined) => {
  if (!subscription) {
    return StripeUserType.WithoutStripe
  }
  if (subscription.isExpired) {
    return StripeUserType.Expired
  }

  return StripeUserType.Active
}

export const BrandSubscriptionOverview = () => {
  const { t } = useTranslation('common')
  const { region } = useRegion()
  const router = useRouter()
  const { userData, refetchAndSetUserData } = useAuth()
  const { isOpen, onClose, onOpen } = useDisclosure()

  const successToast = useSuccessToast()
  const [bespokeTokenCheck, { data }] = useBespokeTokenCheckLazyQuery()
  const [upgradeToBespoke, { loading: isUpgradeToBespokeMutationLoading }] =
    useUpgradeToBespokeMutation()
  const { refetchActiveSubscription } = useHasActiveSubscription({
    brandId: userData?.activeBrand?.id,
  })
  const { enterpriseToken, openEnterpriseInfoModal } = router.query
  const stripeCustomerId = userData?.activeBrand?.stripeCustomerId ?? ''
  const stripeSubscriptionId = userData?.activeBrand?.stripeSubscriptionId ?? ''

  const {
    isOpen: isEnterpriseInfoModalOpen,
    onClose: onEnterpriseInfoModalClose,
  } = useDisclosure({ defaultIsOpen: Boolean(openEnterpriseInfoModal) })

  const subscription = useSubscriptionQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      stripeSubscriptionId,
      brandId: userData?.activeBrand?.id ?? 0,
    },
    skip: !stripeSubscriptionId || !userData?.activeBrand?.id,
  })

  const subscriptionInvoices = useInvoicesQuery({
    fetchPolicy: 'cache-and-network',
    variables: { stripeCustomerId },
    skip: !stripeCustomerId,
  })

  const successFeeInvoices = useSuccessFeeInvoiceListQuery({
    fetchPolicy: 'cache-and-network',
    skip: !userData?.id,
    ...(userData && { variables: { userId: userData.id } }),
  })

  const subscriptionData = subscription.data?.subscription
  const userType = getStripeUserType(subscriptionData)

  useEffect(() => {
    void (async () => {
      if (enterpriseToken && typeof enterpriseToken === 'string') {
        const innerData = await bespokeTokenCheck({
          variables: { token: enterpriseToken },
        })
        if (innerData?.data?.bespokeTokenCheck) {
          onOpen()
        }
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getPlanTitle = () => {
    if (
      userType === StripeUserType.Expired ||
      userType === StripeUserType.WithoutStripe
    ) {
      return t('BRAND_PROFILE_PLAN_CHOOSE')
    }

    return subscriptionData?.subscriptionTier
      ? t(SubscriptionTiersToTagLabels[subscriptionData.subscriptionTier])
      : ''
  }

  const getPlanButtonLabel = () => {
    if (
      userType === StripeUserType.Expired ||
      userType === StripeUserType.WithoutStripe
    ) {
      return t('BRAND_PROFILE_PLAN_CHOOSE')
    }

    return t('TRANSLATION_324')
  }

  const getIsLegacy = () => {
    if (!userData) {
      return false
    }

    const currentPlan = SubscriptionPlans[userData.region].find(
      (sub) => sub.subscriptionTier === subscriptionData?.subscriptionTier
    )

    return Boolean(currentPlan?.isLegacy)
  }

  const getPlanPrice = () => {
    if (
      userType === StripeUserType.Expired ||
      userType === StripeUserType.WithoutStripe
    ) {
      return undefined
    }

    return isNumber(subscriptionData?.amount)
      ? currency(subscriptionData!.amount, { fromCents: true }).value
      : undefined
  }

  const getPaymentTitle = () => {
    if (userType === StripeUserType.Active && subscriptionData?.isCanceled) {
      return t('TRANSLATION_365')
    }

    return t('BRAND_PROFILE_SUBSCRIPTION_NEXT_PAY')
  }

  const getPaymentDate = () => {
    if (
      userType !== StripeUserType.Expired &&
      subscriptionData?.currentPeriodEnd
    ) {
      return format(
        new Date(unixSecondsToMilliseconds(subscriptionData.currentPeriodEnd)),
        'dd.MM.yyyy'
      )
    }

    return undefined
  }

  if (subscription.loading) {
    return (
      <Flex
        gap="8px"
        alignItems="center"
        w="100%"
        h="240px"
        justifyContent="center"
      >
        <Spinner size="xl" />
      </Flex>
    )
  }

  return (
    <Box>
      {userType === StripeUserType.Active ? (
        <Flex {...FullWidthStyles}>
          <Divider />
        </Flex>
      ) : (
        <WarningBar {...FullWidthStyles} />
      )}

      <Flex flexDirection={{ base: 'column', lg: 'row' }} mb="32px">
        <PlanCard
          planTitle={getPlanTitle()}
          planPrice={getPlanPrice()}
          buttonLabel={getPlanButtonLabel()}
          isLegacy={getIsLegacy()}
          subscriptionData={subscriptionData}
          mr={{ base: 0, lg: '32px' }}
          mb={{ base: '32px', lg: 0 }}
          upgradeButton={
            (subscriptionData?.subscriptionTier ===
              SubscriptionTier.Enterprise ||
              subscriptionData?.subscriptionTier ===
                SubscriptionTier.EnterpriseNew) &&
            userType === StripeUserType.Active ? undefined : (
              <Button
                onClick={() => {
                  void router.push(Route.BrandPlanSelection())
                }}
                variant="primary"
                w="full"
                h="50px"
                mt="24px"
                fontSize="17px"
              >
                {getPlanButtonLabel()}
              </Button>
            )
          }
        />
        <PaymentCard
          title={getPaymentTitle()}
          paymentDate={getPaymentDate()}
          buttonLabel={t('BRAND_PROFILE_SUBSCRIPTION_MANAGE_PAY')}
          isDisabled={!stripeCustomerId}
          onClick={() => {
            if (userData) {
              /**
               * We can't use next/router or next/link because this
               * isn't a client side transition
               */
              window.location.replace(
                `${location.origin}/api/customer-portal?stripeCustomerId=${stripeCustomerId}&region=${userData.region}`
              )
            }
          }}
        />
      </Flex>

      <Link
        href={PublicFileLinks.brandTermsAndConditions[region]}
        textDecoration="underline"
      >
        {t('TRANSLATION_366')}
      </Link>
      {process.env.NEXT_PUBLIC_ENVIRONMENT_NAME !== 'production' && (
        <NextLink href={Route.BrandPlanSelection()} textDecoration="underline">
          Test Plan selection
        </NextLink>
      )}
      <EnterpriseSubmitModal
        onClose={onClose}
        isOpen={isOpen}
        pricePerMonth={data?.bespokeTokenCheck.pricePerMonth}
        successFee={data?.bespokeTokenCheck.successFee}
        isSubmitLoading={isUpgradeToBespokeMutationLoading}
        onSubmit={() => {
          void (async () => {
            if (!enterpriseToken || typeof enterpriseToken !== 'string') {
              // eslint-disable-next-line no-console
              console.error(
                'enterpriseToken is of wrong format. should not happen'
              )
              return
            }
            const response = await upgradeToBespoke({
              variables: { token: enterpriseToken },
            })
            if (response?.data?.upgradeToBespoke.wasSuccessfullyUpdated) {
              // TODO: currently this refetching causes blinking, this is
              //  because of conditional rendering earlier. we should
              //  definitely do some kind of loading to the cards,
              //  instead of white blank page.
              //  https://github.com/sudolabs-io/EzTenda/issues/1685
              /**
               * even if the invoices.refetch() is called, the invoice is
               * actually generated a little bit later than right when the
               * update happens, so another refresh of the page is necessary
               */
              await Promise.all([
                subscription.refetch(),
                subscriptionInvoices.refetch(),
                successFeeInvoices.refetch(),
                refetchAndSetUserData(),
                refetchActiveSubscription(),
              ])
              successToast({
                title: t('TRANSLATION_367'),
                description: t('TRANSLATION_368'),
              })
              void router.push(Route.BrandSubscriptionOverview(), undefined, {
                shallow: true,
              })
              onClose()
            } else if (
              response?.data?.upgradeToBespoke.needsRedirect &&
              response.data.upgradeToBespoke.redirectUrl
            ) {
              window.location.assign(response.data.upgradeToBespoke.redirectUrl)
            }
          })()
        }}
      />
      <EnterpriseInfoModal
        isOpen={isEnterpriseInfoModalOpen}
        onClose={onEnterpriseInfoModalClose}
      />
    </Box>
  )
}
